import { isSSR } from '@helpers/ssr';
import { withMotion } from '@hoc/withMotion';
import useMobileNavOffset from '@hooks/useMobileNavOffset';
import DropdownIcon from '@img/icons/icon-arrow-down.svg';
import FilterIcon from '@img/icons/icon-filter.svg';
import Plus from '@img/icons/icon-plus.svg';
import Reset from '@img/icons/icon-restart.svg';
import X from '@img/icons/x.svg';
import { RootState } from '@state/ducks';
import Button from '@stories/Elements/Forms/Buttons/Button';
import FilterDropdown from '@stories/Elements/ProductFilters/FilterDropdown/FilterDropdown';
import { AnimatePresence } from 'framer-motion';
import React, { FunctionComponent } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  DecodedValueMap,
  NumberParam,
  QueryParamConfig,
  SetQuery,
  decodeDelimitedArray,
  encodeDelimitedArray,
  useQueryParam,
  withDefault,
} from 'use-query-params';
import { ProductListingFiltersStyles as S } from './ProductListingFilters.styles';

export interface ProductListingFiltersProps {
  translations: Record<string, string | null>;
  response: ProductListResponse | null;
  filters: ProductFilterViewModel[];
  urlFilters: ProductFilterViewModel[] | null;
  sortNewestByDefault?: boolean;
  groupByCategory?: boolean;
  enableCompare?: boolean;
  isLoading: boolean;
  expandAllFilters?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilterValue: (filters: DecodedValueMap<any>) => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: DecodedValueMap<any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: SetQuery<any>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CommaArrayParam: QueryParamConfig<string[], any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  encode: (array: any) => encodeDelimitedArray(array, ','),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decode: (arrayStr: any) => decodeDelimitedArray(arrayStr, ','),
};

const ProductListingFilters: FunctionComponent<ProductListingFiltersProps> = ({
  translations,
  response,
  filters,
  setFilterValue,
  isLoading,
  value,
  setValue,
  expandAllFilters,
}) => {
  const sortByFilter = React.useMemo(() => response?.filters.find((x) => x.key === 'orderby'), [response]);
  const [, setPageIndex] = useQueryParam('pageindex', withDefault(NumberParam, 0));
  const [openFilters] = useQueryParam<string[]>(
    'openfilters',
    withDefault(CommaArrayParam, expandAllFilters ? filters.map((x) => x.key) : []),
  );
  const [showMobileFilters, setShowMobileFilters] = React.useState<boolean>(false);
  const topOffset = useMobileNavOffset();
  const filtersInnerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const bodyElement = document.querySelector('body');
    if (bodyElement) {
      if (showMobileFilters) {
        let existingStyleAttribute = bodyElement.getAttribute('style') || '';
        existingStyleAttribute += 'max-height: 100vh; overflow: hidden;';
        bodyElement.setAttribute('style', existingStyleAttribute);
      }
    }

    return function () {
      const bodyElement = document.querySelector('body');
      if (bodyElement) {
        let existingStyleAttribute = bodyElement.getAttribute('style') || '';
        existingStyleAttribute = existingStyleAttribute.replace('max-height: 100vh; overflow: hidden;', '');
        bodyElement.setAttribute('style', existingStyleAttribute);
      }
    };
  }, [showMobileFilters]);

  // Compare
  const { showCompare, items } = useSelector((x: RootState) => x.compare);

  function handleScroll(): void {
    if (filtersInnerRef.current) {
      const boundingRect = filtersInnerRef.current.getBoundingClientRect();
      filtersInnerRef.current.style.setProperty('max-height', `calc(100dvh - ${boundingRect.y + 50}px)`);
    }
  }

  React.useEffect(() => {
    if (filtersInnerRef.current) {
      handleScroll();
    }

    window.addEventListener('scroll', handleScroll);

    return function () {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filtersInnerRef.current]);

  React.useEffect(() => {
    setTimeout(() => {
      handleScroll();
    }, 600);
  }, [items.length]);

  const anyFiltersSelected = React.useMemo(
    () =>
      Object.keys(value).reduce<boolean>((acc, cur) => {
        if (cur === 'orderby') {
          return acc;
        }

        const filter: string[] = value[cur];
        if (filter.length > 0) {
          acc = true;
        }

        return acc;
      }, false),
    [value],
  );

  const [filtersDOMElement, setFiltersDOMElement] = React.useState<HTMLDivElement | null>(null);
  React.useEffect(() => {
    setFiltersDOMElement(document.querySelector("div[data-filters-bar='true']") as HTMLDivElement | null);
  }, []);

  const _setValue = (_value: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [x: string]: any;
  }): void => {
    setValue(_value);
    setFilterValue(_value); // Update external state too to fetch data //
  };

  const onFilterChange = async (key: string, selectedValues: string[], replace = false): Promise<void> => {
    const collectionToUse = value;
    const existingFilter = collectionToUse[key];

    // Reset the page index
    setPageIndex(0);

    if (replace === true) {
      const newFilters = { ...collectionToUse, [key]: [...selectedValues] };
      _setValue(newFilters);
      return;
    }

    if (selectedValues.length === 0) {
      const newFilters = { ...collectionToUse, [key]: [] };
      _setValue(newFilters);
      return;
    }

    if (selectedValues.length > 1) {
      const newFilters = { ...collectionToUse, [key]: selectedValues };
      _setValue(newFilters);
      return;
    }

    const selectedValue = selectedValues[0];

    const existingValues = [...existingFilter] as string[];

    const existingIndex = existingValues.indexOf(selectedValue);

    if (existingIndex === -1) {
      existingValues.push(selectedValue);
    } else {
      existingValues.splice(existingIndex, 1);
    }

    const newFilters = { ...collectionToUse, [key]: existingValues };
    _setValue(newFilters);
  };

  const getSortByFilter = (): JSX.Element => (
    <>
      {sortByFilter && (
        <S.ThemedFilterSort
          filtersOpen={false} // Only seems to add some borders for the old filters //
          key={sortByFilter.key}
          viewModel={sortByFilter}
          selectedValue={value['orderby'][0]}
          onChange={(key, value) => {
            onFilterChange(key, value, true);
          }}
        />
      )}
    </>
  );

  const FilterList = (
    <>
      <S.FiltersList>
        {filters.filter((x) => x.filterOptions.length > 0) &&
          filters.map((x) => {
            const viewModel = filters.find((f) => f.key === x.key);
            const activeViewModel = response?.filters.find((f) => f.key === x.key);

            if (!viewModel) {
              return null;
            }

            // Hide the child category filter //
            if (x.key === 'childCategory') {
              return null;
            }

            return (
              <S.FilterWrapper key={x.key}>
                <FilterDropdown
                  viewModel={viewModel}
                  activeViewModel={activeViewModel}
                  onChange={onFilterChange}
                  disabled={isLoading}
                  // We need a ref to the value here for some reason, it just never has the up-to-date value otherwise //
                  selectedValues={value[x.key]}
                  listLinks={false}
                  showAllButton={false}
                  showCount={!['showUnpublishedProducts', 'perfectMatch'].includes(x.key)}
                  translations={translations}
                  theme="border-accordion"
                  activeByDefault={openFilters.includes(x.key)}
                />
              </S.FilterWrapper>
            );
          })}
      </S.FiltersList>
    </>
  );

  const tags = React.useMemo(() => {
    // Reduce the values in the filters to single tags with data stored on them //
    const _tags: JSX.Element[] = [];
    Object.keys(value).forEach((key) => {
      if (key === 'orderby') {
        return;
      }

      const filterData = filters.find((x) => x.key === key);
      if (filterData) {
        const filter: string[] = value[key];
        filter.forEach((filterValue) => {
          // Find the matching value //
          const matchingValue = filterData.filterOptions.find((x) => x.value === filterValue);
          if (matchingValue) {
            _tags.push(
              <S.Tag
                key={`ProductListingFilters_${key}_${matchingValue.value}`}
                onClick={() => {
                  // Get filter value //
                  const clone = { ...value };
                  const filterParent = [...clone[key]];
                  const index = filterParent.indexOf(filterValue);
                  filterParent.splice(index, 1);
                  clone[key] = filterParent;
                  setValue(clone);
                  setFilterValue(clone);
                }}
              >
                <span>{matchingValue.name}</span> <X />
              </S.Tag>,
            );
          }
        });
      }
    });

    return _tags;
  }, [value, filters]);

  return (
    <>
      {filters.length > 0 && (
        <S.Wrapper data-compare-open={showCompare && items.length > 0}>
          <S.Container>
            <S.TopBar>
              <S.TopBarIcon>
                <FilterIcon />
              </S.TopBarIcon>
              {translations['custom.filters.filters']}
            </S.TopBar>
            <S.DesktopLayout>
              <S.FiltersInner ref={filtersInnerRef}>
                {tags.length > 0 && (
                  <S.TagList data-disabled={isLoading} aria-hidden={isLoading ? 'true' : 'false'}>
                    {tags}
                  </S.TagList>
                )}
                {FilterList}
              </S.FiltersInner>
            </S.DesktopLayout>
          </S.Container>
          <AnimatePresence>
            {showMobileFilters && (
              <S.MobileFilters data-mobile-nav-offset={topOffset} key={`ProductListingFilters_MobileMenu`}>
                <S.MobileFiltersInner>
                  <S.MobileTopBar>
                    <S.TopBarIcon>
                      <FilterIcon />
                    </S.TopBarIcon>
                    <span>{translations['custom.filters.filter']}</span>
                    <S.MobileClose onClick={() => setShowMobileFilters(false)}>
                      {translations['custom.filters.close']}
                      <Plus />
                    </S.MobileClose>
                  </S.MobileTopBar>
                  <S.MobileFiltersContent>
                    <Button
                      text={translations['custom.filters.apply'] || 'Apply Filters'}
                      onClick={() => {
                        setShowMobileFilters(false);
                      }}
                    />
                    {tags.length > 0 && (
                      <S.TagList data-disabled={isLoading} aria-hidden={isLoading ? 'true' : 'false'}>
                        {tags}
                      </S.TagList>
                    )}
                    {FilterList}
                    <Button
                      text={translations['custom.filters.apply'] || 'Apply Filters'}
                      onClick={() => {
                        setShowMobileFilters(false);
                      }}
                    />
                  </S.MobileFiltersContent>
                </S.MobileFiltersInner>
              </S.MobileFilters>
            )}
          </AnimatePresence>
        </S.Wrapper>
      )}
      {!isSSR &&
        filtersDOMElement &&
        createPortal(
          <S.TopFilters mobileFiltersOpen={showMobileFilters}>
            {filters.length > 0 && (
              <>
                <S.ResetCompare>
                  <S.ResetButton
                    onClick={() => {
                      const resetFilters = Object.keys(value).reduce<Record<string, never[]>>((acc, cur) => {
                        acc[cur] = [];
                        return acc;
                      }, {});

                      setValue(resetFilters); // Filters //
                      setFilterValue(resetFilters); // Parent state to refetch from API //
                    }}
                    disabled={!anyFiltersSelected}
                  >
                    <Reset />
                    {translations['custom.filters.reset']}
                  </S.ResetButton>
                </S.ResetCompare>
                <S.MobileFiltersWrapper onClick={() => setShowMobileFilters(true)}>
                  <S.TopBarIcon>
                    <FilterIcon />
                  </S.TopBarIcon>
                  {translations['custom.filters.filter']}
                  <S.MobileDropdownIcon>
                    <DropdownIcon />
                  </S.MobileDropdownIcon>
                </S.MobileFiltersWrapper>
              </>
            )}
            <S.SortByWrapper>{getSortByFilter()}</S.SortByWrapper>
          </S.TopFilters>,
          filtersDOMElement,
        )}
    </>
  );
};

export default withMotion(ProductListingFilters);
